import { Stack, styled, Typography, useTheme } from '@mui/material';
import { ReactElement } from 'react';
import { FormLabel, Tooltip } from 'src/components';
import { SidePanelRow } from 'src/components/SidePanelRow';
import { GetStripeSubscriptionDetailsResponse } from 'src/graphql/entities/stripe-subscription-details';
import { useSubscriptionDetailsSummaryFields } from './use-subscription-details-summary';

const PanelContainer = styled(Stack, {
  name: 'PanelContainer',
})(({ theme }) => ({
  alignItems: 'stretch',
  justifyContent: 'flex-end',
  width: '100%',
  gap: theme.spacing(0.5),

  '& > *': {
    flex: 1,
  },

  '& .Mui-error .MuiInput-input:not(:placeholder-shown)': {
    color: theme.palette.error.main,
  },
}));

const PanelField = (props: {
  value: string | number | ReactElement;
  label: string;
  icon: ReactElement;
  tooltip?: string;
  endAdornment?: ReactElement;
  disabled?: boolean;
  color?: string;
}) => {
  const theme = useTheme();
  const { value, label, icon, tooltip, endAdornment, disabled, color } = props;
  const valueCell = (
    <Typography
      component="span"
      sx={{
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        color: disabled ? theme.palette.text.disabled : color,
      }}
    >
      {icon}
      {value}
      {endAdornment}
    </Typography>
  );

  return (
    <SidePanelRow key={label}>
      <FormLabel label={label} />
      <Stack
        direction="row"
        alignItems="center"
        width="100%"
        gap={1}
        padding={theme.spacing(1, 0.5)}
      >
        {tooltip ? <Tooltip title={tooltip}>{valueCell}</Tooltip> : valueCell}
      </Stack>
    </SidePanelRow>
  );
};

interface Props {
  subscriptionDetails: GetStripeSubscriptionDetailsResponse | undefined;
}

export const SubscriptionDetailsSummary = ({ subscriptionDetails }: Props) => {
  const { fields } = useSubscriptionDetailsSummaryFields({
    subscriptionDetails,
  });

  return (
    <PanelContainer>
      {fields.map((field) => (
        <PanelField {...field} key={field.id} />
      ))}
    </PanelContainer>
  );
};
